import { apiClient } from '@/core/api/client';
import { logger } from '@/core/logger';
import { RegistrationResponse } from '@/modules/onboarding/registration/store/registrationStore';
import * as FileSystemLegacy from 'expo-file-system/legacy';
import * as ImageManipulator from 'expo-image-manipulator';
import {
    generateDocumentFileName,
    getApiDocumentType,
    getDocIdFromRegistrationResponse,
    type DocumentType,
} from '../utils/documentTypeMapping';

export interface DocumentUploadRequest {
    fileName: string;
    documentType: 'EID_FRONT' | 'EID_BACK' | 'PASSPORT';
    contentLocation: 'S'; // 'S' for Salesforce
    customerId: string;
    parentId: string; // registration_id
    fileContent: string; // Base64 encoded
}

export interface DocumentUploadResponse {
    status: 'success' | 'error';
    message: string;
    details?: {
        uploadedDocuments: {
            name: string;
            contentDocumentId: string;
            status: 'uploaded' | 'error';
            documentType: string;
        }[];
    };
}

export interface UploadDocumentParams {
    documentType: DocumentType;
    fileUri: string;
    registrationResponse: RegistrationResponse;
}

export interface UploadDocumentResult {
    success: boolean;
    contentDocumentId?: string;
    error?: string;
}

/**
 * Compress image for upload to prevent 413 errors
 * Base64 encoding increases size by ~33%, so we compress aggressively
 * Supports up to 5MB max file upload
 * Returns the compressed file URI
 */
async function compressImageForUpload(fileUri: string): Promise<string> {
    try {
        const fileInfo = await FileSystemLegacy.getInfoAsync(fileUri);
        if (!fileInfo.exists) {
            throw new Error('File does not exist');
        }

        const fileSize = fileInfo.size || 0;
        const fileSizeMB = (fileSize / (1024 * 1024)).toFixed(2);
        
        // Target: Keep base64 size under ~4.7MB (file size ~3.5MB)
        // Base64 overhead is ~33%, so 3.5MB file = ~4.66MB base64
        // Max: 5MB base64 upload limit, so we target 4.7MB base64 (safe buffer)
        const targetMaxSizeBytes = 3.5 * 1024 * 1024; // 3.5MB target file size
        const maxSizeBytes = 4.0 * 1024 * 1024; // 4MB file size before aggressive compression

        logger.info('DocumentUploadService', 'Checking image size for upload', {
            fileUri,
            fileSize: fileSize,
            fileSizeMB: `${fileSizeMB} MB`,
            targetMaxSizeBytes: `${(targetMaxSizeBytes / (1024 * 1024)).toFixed(2)} MB`,
            maxSizeBytes: `${(maxSizeBytes / (1024 * 1024)).toFixed(2)} MB`,
        });

        // If file is already small enough, still compress slightly for consistency
        // Always compress to ensure consistent quality and size
        // For 5MB limit, we can use higher quality settings
        let resizeWidth = 3000;
        let resizeHeight = 3000;
        let compressQuality = 0.85; // 85% quality (higher quality for 5MB limit)

        // If file is larger than target, use more aggressive compression
        if (fileSize > targetMaxSizeBytes) {
            resizeWidth = 2500;
            resizeHeight = 2500;
            compressQuality = 0.75; // 75% quality
            logger.info('DocumentUploadService', 'Using aggressive compression', {
                resizeWidth,
                resizeHeight,
                compressQuality,
            });
        }

        // If file is very large, use maximum compression
        if (fileSize > maxSizeBytes) {
            resizeWidth = 2000;
            resizeHeight = 2000;
            compressQuality = 0.7; // 70% quality
            logger.info('DocumentUploadService', 'Using maximum compression', {
                resizeWidth,
                resizeHeight,
                compressQuality,
            });
        }

        logger.info('DocumentUploadService', 'Compressing image for upload', {
            fileUri,
            originalSize: fileSize,
            resizeWidth,
            resizeHeight,
            compressQuality,
        });

        const compressed = await ImageManipulator.manipulateAsync(
            fileUri,
            [
                {
                    resize: {
                        width: resizeWidth,
                        height: resizeHeight,
                    },
                },
            ],
            {
                compress: compressQuality,
                format: ImageManipulator.SaveFormat.JPEG,
            }
        );

        const compressedInfo = await FileSystemLegacy.getInfoAsync(compressed.uri);
        const compressedSize = compressedInfo.exists && 'size' in compressedInfo ? compressedInfo.size : 0;
        
        // Estimate base64 size (base64 is ~33% larger)
        let estimatedBase64Size = compressedSize * 1.33;
        
        const compressedSizeMB = (compressedSize / (1024 * 1024)).toFixed(2);
        const estimatedBase64SizeMB = (estimatedBase64Size / (1024 * 1024)).toFixed(2);
        
        logger.info('DocumentUploadService', 'Image compressed for upload', {
            originalSize: fileSize,
            originalSizeMB: `${fileSizeMB} MB`,
            compressedSize: compressedSize,
            compressedSizeMB: `${compressedSizeMB} MB`,
            estimatedBase64Size: Math.round(estimatedBase64Size),
            estimatedBase64SizeMB: `${estimatedBase64SizeMB} MB`,
            reduction: compressedSize > 0 ? `${((1 - compressedSize / fileSize) * 100).toFixed(1)}%` : 'N/A',
        });

        // If base64 size is still too large, compress further recursively
        // Target: Keep base64 under 4.5MB to prevent 413 errors (conservative limit, 5MB max)
        const maxBase64Size = 4.5 * 1024 * 1024; // 4.5MB base64 limit (conservative, 5MB max upload)
        let currentUri = compressed.uri;
        let currentSize = compressedSize;
        let currentQuality = compressQuality;
        let currentWidth = resizeWidth;
        let currentHeight = resizeHeight;
        let iteration = 0;
        const maxIterations = 5; // Prevent infinite loops

        while (estimatedBase64Size > maxBase64Size && iteration < maxIterations) {
            iteration++;
            logger.warn('DocumentUploadService', `Base64 size still too large (iteration ${iteration}), applying additional compression`, {
                estimatedBase64Size: Math.round(estimatedBase64Size),
                currentSize,
                currentQuality,
                currentWidth,
                currentHeight,
            });

            // Progressively reduce quality and size
            currentQuality = Math.max(0.5, currentQuality - 0.1); // Reduce by 10%, minimum 50% (higher min for 5MB limit)
            currentWidth = Math.max(1200, Math.floor(currentWidth * 0.85)); // Reduce by 15%, minimum 1200px (higher min for 5MB limit)
            currentHeight = Math.max(1200, Math.floor(currentHeight * 0.85));

            logger.info('DocumentUploadService', `Applying compression iteration ${iteration}`, {
                newQuality: currentQuality,
                newWidth: currentWidth,
                newHeight: currentHeight,
            });

            const furtherCompressed = await ImageManipulator.manipulateAsync(
                currentUri,
                [
                    {
                        resize: {
                            width: currentWidth,
                            height: currentHeight,
                        },
                    },
                ],
                {
                    compress: currentQuality,
                    format: ImageManipulator.SaveFormat.JPEG,
                }
            );

            const furtherCompressedInfo = await FileSystemLegacy.getInfoAsync(furtherCompressed.uri);
            const furtherCompressedSize = furtherCompressedInfo.exists && 'size' in furtherCompressedInfo ? furtherCompressedInfo.size : 0;
            const newEstimatedBase64Size = furtherCompressedSize * 1.33;

            const previousSizeMB = (currentSize / (1024 * 1024)).toFixed(2);
            const newSizeMB = (furtherCompressedSize / (1024 * 1024)).toFixed(2);
            const previousBase64MB = (estimatedBase64Size / (1024 * 1024)).toFixed(2);
            const newBase64MB = (newEstimatedBase64Size / (1024 * 1024)).toFixed(2);
            
            logger.info('DocumentUploadService', `Compression iteration ${iteration} completed`, {
                previousSize: currentSize,
                previousSizeMB: `${previousSizeMB} MB`,
                newSize: furtherCompressedSize,
                newSizeMB: `${newSizeMB} MB`,
                previousEstimatedBase64: Math.round(estimatedBase64Size),
                previousEstimatedBase64MB: `${previousBase64MB} MB`,
                newEstimatedBase64: Math.round(newEstimatedBase64Size),
                newEstimatedBase64MB: `${newBase64MB} MB`,
            });

            // Update for next iteration
            currentUri = furtherCompressed.uri;
            currentSize = furtherCompressedSize;
            estimatedBase64Size = newEstimatedBase64Size;
        }

        const finalSizeMB = (currentSize / (1024 * 1024)).toFixed(2);
        const finalBase64MB = (estimatedBase64Size / (1024 * 1024)).toFixed(2);
        
        if (estimatedBase64Size > maxBase64Size) {
            logger.error('DocumentUploadService', 'Failed to compress image below size limit after maximum iterations', {
                finalSize: currentSize,
                finalSizeMB: `${finalSizeMB} MB`,
                finalEstimatedBase64: Math.round(estimatedBase64Size),
                finalEstimatedBase64MB: `${finalBase64MB} MB`,
                maxBase64SizeMB: `${(maxBase64Size / (1024 * 1024)).toFixed(2)} MB`,
                iterations: iteration,
            });
            // Still return the most compressed version - let API handle rejection if needed
        } else {
            logger.info('DocumentUploadService', 'Image successfully compressed below size limit', {
                finalSize: currentSize,
                finalSizeMB: `${finalSizeMB} MB`,
                finalEstimatedBase64: Math.round(estimatedBase64Size),
                finalEstimatedBase64MB: `${finalBase64MB} MB`,
                maxBase64SizeMB: `${(maxBase64Size / (1024 * 1024)).toFixed(2)} MB`,
                iterations: iteration,
            });
        }

        return currentUri;
    } catch (error) {
        logger.error('DocumentUploadService', 'Failed to compress image', {
            fileUri,
            error,
        });
        // Return original URI if compression fails (will likely fail upload, but better than crashing)
        return fileUri;
    }
}

/**
 * Convert file URI to base64 string
 */
async function fileUriToBase64(fileUri: string): Promise<string> {
    try {
        const base64 = await FileSystemLegacy.readAsStringAsync(fileUri, {
            encoding: (FileSystemLegacy as any).EncodingType?.Base64 ?? (FileSystemLegacy as any).EncodingType,
        });
        return base64;
    } catch (error) {
        logger.error('DocumentUploadService', 'Failed to convert file to base64', {
            fileUri,
            error,
        });
        throw new Error('Failed to convert file to base64');
    }
}

/**
 * Upload a single document to the API
 */
export async function uploadDocument({
    documentType,
    fileUri,
    registrationResponse,
}: UploadDocumentParams): Promise<UploadDocumentResult> {
    try {
        // Validate registration response
        if (!registrationResponse.customer_id || !registrationResponse.registration_id) {
            throw new Error('Missing customer_id or registration_id in registration response');
        }

        // Get doc_id from registration response
        const docId = getDocIdFromRegistrationResponse(documentType, registrationResponse);
        if (!docId) {
            throw new Error(`No doc_id found for document type: ${documentType}`);
        }

        // Always compress image for upload to prevent 413 errors (base64 increases size by ~33%)
        let compressedUri = await compressImageForUpload(fileUri);

        // Convert file to base64 and check actual size
        let fileContent = await fileUriToBase64(compressedUri);
        // Base64 string size in bytes = string length (each character is 1 byte)
        let base64SizeBytes = fileContent.length;
        const base64SizeMB = (base64SizeBytes / (1024 * 1024)).toFixed(2);
        
        logger.info('DocumentUploadService', 'Base64 size check', {
            base64SizeBytes: Math.round(base64SizeBytes),
            base64SizeMB: `${base64SizeMB} MB`,
        });

        // If base64 is still too large, compress further with maximum settings
        // Target: 4.8MB base64 (safe buffer below 5MB max upload limit)
        const maxBase64SizeBytes = 4.8 * 1024 * 1024; // 4.8MB absolute limit (5MB max upload)
        let compressionIteration = 0;
        const maxCompressionIterations = 3;

        while (base64SizeBytes > maxBase64SizeBytes && compressionIteration < maxCompressionIterations) {
            compressionIteration++;
            const currentBase64SizeMB = (base64SizeBytes / (1024 * 1024)).toFixed(2);
            logger.warn('DocumentUploadService', `Base64 still too large after compression (${compressionIteration}), applying maximum compression`, {
                base64SizeBytes: Math.round(base64SizeBytes),
                base64SizeMB: `${currentBase64SizeMB} MB`,
                maxBase64SizeMB: `${(maxBase64SizeBytes / (1024 * 1024)).toFixed(2)} MB`,
            });

            // Apply maximum compression
            const maxCompressed = await ImageManipulator.manipulateAsync(
                compressedUri,
                [
                    {
                        resize: {
                            width: 2000,
                            height: 2000,
                        },
                    },
                ],
                {
                    compress: 0.6, // 60% quality - less aggressive for 5MB limit
                    format: ImageManipulator.SaveFormat.JPEG,
                }
            );

            compressedUri = maxCompressed.uri;
            fileContent = await fileUriToBase64(compressedUri);
            const newBase64SizeBytes = fileContent.length;

            const newBase64SizeMB = (newBase64SizeBytes / (1024 * 1024)).toFixed(2);
            logger.info('DocumentUploadService', `Maximum compression iteration ${compressionIteration} completed`, {
                previousBase64Size: Math.round(base64SizeBytes),
                previousBase64SizeMB: `${currentBase64SizeMB} MB`,
                newBase64Size: Math.round(newBase64SizeBytes),
                newBase64SizeMB: `${newBase64SizeMB} MB`,
            });

            base64SizeBytes = newBase64SizeBytes;

            // If still too large, try even more aggressive compression
            if (base64SizeBytes > maxBase64SizeBytes && compressionIteration < maxCompressionIterations) {
                const ultraCompressed = await ImageManipulator.manipulateAsync(
                    compressedUri,
                    [
                        {
                            resize: {
                                width: 1500,
                                height: 1500,
                            },
                        },
                    ],
                    {
                        compress: 0.5, // 50% quality - less aggressive for 5MB limit
                        format: ImageManipulator.SaveFormat.JPEG,
                    }
                );

                compressedUri = ultraCompressed.uri;
                fileContent = await fileUriToBase64(compressedUri);
                base64SizeBytes = fileContent.length;
                compressionIteration++;

                const ultraBase64SizeMB = (base64SizeBytes / (1024 * 1024)).toFixed(2);
                logger.info('DocumentUploadService', `Ultra compression iteration ${compressionIteration} completed`, {
                    base64SizeBytes: Math.round(base64SizeBytes),
                    base64SizeMB: `${ultraBase64SizeMB} MB`,
                });
            }
        }

        const finalBase64SizeMB = (base64SizeBytes / (1024 * 1024)).toFixed(2);
        const maxBase64SizeMB = (maxBase64SizeBytes / (1024 * 1024)).toFixed(2);
        
        if (base64SizeBytes > maxBase64SizeBytes) {
            logger.error('DocumentUploadService', 'Base64 size still too large after maximum compression', {
                finalBase64Size: Math.round(base64SizeBytes),
                finalBase64SizeMB: `${finalBase64SizeMB} MB`,
                maxBase64SizeMB: `${maxBase64SizeMB} MB`,
                iterations: compressionIteration,
            });
        } else {
            logger.info('DocumentUploadService', 'Base64 size acceptable for upload', {
                finalBase64Size: Math.round(base64SizeBytes),
                finalBase64SizeMB: `${finalBase64SizeMB} MB`,
                maxBase64SizeMB: `${maxBase64SizeMB} MB`,
                iterations: compressionIteration,
            });
        }

        // Generate file name
        const fileName = generateDocumentFileName(documentType);

        // Get API document type
        const apiDocumentType = getApiDocumentType(documentType);

        // Prepare upload request
        const uploadRequest: { documents: DocumentUploadRequest[] } = {
            documents: [
                {
                    fileName,
                    documentType: apiDocumentType,
                    contentLocation: 'S',
                    customerId: registrationResponse.customer_id,
                    parentId: registrationResponse.registration_id,
                    fileContent,
                },
            ],
        };

        logger.info('DocumentUploadService', 'Uploading document', {
            documentType: documentType,
            apiDocumentType,
            fileName,
            customerId: registrationResponse.customer_id,
            registrationId: registrationResponse.registration_id,
            docId,
            fileUri,
        });

        // Make API call
        const response = await apiClient.post<DocumentUploadResponse>(
            '/v1/document/upload',
            uploadRequest
        );

        if (response.status === 'success' && response.details?.uploadedDocuments?.[0]) {
            const uploadedDoc = response.details.uploadedDocuments[0];
            logger.info('DocumentUploadService', 'Document uploaded successfully', {
                documentType,
                contentDocumentId: uploadedDoc.contentDocumentId,
            });

            return {
                success: true,
                contentDocumentId: uploadedDoc.contentDocumentId,
            };
        } else {
            throw new Error(response.message || 'Upload failed');
        }
    } catch (error: any) {
        logger.error('DocumentUploadService', 'Failed to upload document', {
            documentType,
            error: error?.response?.data || error?.message || error,
        });

        return {
            success: false,
            error: error?.response?.data?.message || error?.message || 'Upload failed',
        };
    }
}

